import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useTour } from '../../../../context/TourContext';
import InstructorAssignmentForm from '../../../UI/instructor/settings/assignments/form';
import InstructorLevelItemsModal from '../../../UI/instructor/settings/assignments/levelItemsModal';
import BalancedEquationLevelModal from '../../../UI/instructor/settings/assignments/balancedEquationLevelModal';
import PrinciplesLevelModal from '../../../UI/instructor/settings/assignments/principlesLevelModal';
import ClassificationLevelModal from '../../../UI/instructor/settings/assignments/classificationLevelModal';
import NormalBalanceLevelModal from '../../../UI/instructor/settings/assignments/normalBalanceLevelModal';

import { 
  navigation,
  gameCreateItemsQuestionsColumns,
  gameCreateItemsOptionsClassification,
  gameCreateItemsOptionsNormal,
  scenarioLevelsDescription,
  designTypesMap
} from './data';

import {
  generalInfoQuery,
  instructorScenarioLevelsQuery,
  getInstructorScenarioLevels,
  createScenario,
  createGame,
  createScenarioLevels,
  createItemQuestionsBulk,
  createItemOptionsBulk,
  scenarioAddCourse,
  coursesSetupQuery
} from './api';

import { formatScnearioLevelToFilterName, shuffleArray, getTargetEntityType, matchesEntityType } from '../../../../utils';
import SubPagesLayout from '../../../UI/layout/subPages';

export default function InstructorSettingsAddAssignments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isActive: isTourActive, nextStep: tourNextStep } = useTour();

  const [isLoading, setIsLoading] = useState(false);
  const [gameCreatedId, setGameCreatedId] = useState(0);

  const [isModalShown, setIsModalShown] = useState(false);

  // One source-template scenario per game type — replaces the prior
  // multi-source lookups (be-corporate / be-noncorporate for equation,
  // principles-corporate / principles-noncorporate / principles-
  // assumptions-constraints for principles). Templates are pulled
  // from these single scenarios at the level-creation step.
  const [beGameScenarioId, setBeGameScenarioId] = useState(null);
  const [principlesGameScenarioId, setPrinciplesGameScenarioId] = useState(null);

  const [gameType, setGameType] = useState();

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const games = generalInfo?.games;
  const courses = generalInfo?.courses;

  const { data: scenarios, refetch: refetchScenarios } = useQuery(coursesSetupQuery());

  // Instructor-scoped endpoint (matches edit.js / edit-tournament.js).
  // The non-instructor scenarioLevelsQuery returns [] for template
  // scenarios the instructor doesn't own, which left the BE/Principles
  // level modal empty on this page even after the template was linked.
  const { data: beGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: beGameScenarioId }));
  const { data: principlesGameScenarioLevels } = useQuery(instructorScenarioLevelsQuery({ scenarioId: principlesGameScenarioId }));

  const { refetch: refetchCoursesSetup } = useQuery({
    ...coursesSetupQuery(),
    enabled: false
  });


  const [createdScenarioId, setCreatedScenarioId] = useState(0);

  const createScenarioMutation = useMutation({
    mutationFn: (data) => createScenario(data)
  });
  
  const createGameMutation = useMutation({
    mutationFn: (data) => createGame(data)
  });
  
  const createScenarioLevelsMutation = useMutation({
    mutationFn: (data) => createScenarioLevels(data)
  });
  
  const createItemQuestionsBulkMutation = useMutation({
    mutationFn: (data) => createItemQuestionsBulk(data)
  });
  
  const scenarioAddCourseMutation = useMutation({
    mutationFn: (data) => scenarioAddCourse(data)
  });
  
  const createItemOptionsBulkMutation = useMutation({
    mutationFn: (data) => createItemOptionsBulk(data)
  });

  useEffect(() => {
    if(games && !games.length) {
      refetchScenarios();
    }
  }, [games]);

  // Resolve a single source-template scenario per game type from the
  // course-setup payload. Replaces the previous multi-source pattern.
  useEffect(() => {
    if (!scenarios || !games) return;
    if (gameType === 'Balanced Equation' || !games.length) {
      setBeGameScenarioId(scenarios.find(s => s.name === 'be-game')?.id || null);
    }
    if (gameType === 'The Principles' || !games.length) {
      setPrinciplesGameScenarioId(scenarios.find(s => s.name === 'principles-game')?.id || null);
    }
  }, [scenarios, games, gameType]);

  const mapBeQuestionToFilterItem = ({ transaction_id, order, description }) => ({
    id: transaction_id,
    order,
    text: description,
    column: 'Transactions',
  });

  const mapPrinciplesTransactionToFilterItem = ({ id, order, description, transaction_id }) => ({
    id,
    order,
    text: description,
    column: 'Transactions',
    accountItemId: transaction_id,
  });

  // Per-game-type filter map. Top-level keys are decision-type labels;
  // sub-buckets are template scenario levels matched to that decision
  // type per the single-source rules:
  //   equation    (be-game)        → all levels under every BE decision type
  //   principles  (principles-game) → "Revenue & Expense Recognition" = all design.includes('2'),
  //                                   "Assumptions, Principles, & Constraints" = all design.includes('4')
  // (Classification + Normal level 1 is auto-created on this add page;
  //  templates only matter on edit.js.)
  const targetEntityType = getTargetEntityType(courses?.[0]?.businesstype);

  const filterItems = useMemo(() => {
    // Narrow source-template levels to those matching the course's
    // business-type before building filter buckets. For BE / Principles
    // the entity_type lives directly on the scenario level. Levels
    // tagged 'both', null, or arrays containing both values pass
    // through (see matchesEntityType).
    const filterLevelsByEntity = (levels) =>
      (levels || []).filter(l => matchesEntityType(l.entity_type, targetEntityType));

    const buildLevelsBucket = (levels, mapItem, itemsKey) =>
      (levels || []).reduce((acc, level) => {
        acc[formatScnearioLevelToFilterName(level.name)] = (level[itemsKey] || []).map(mapItem);
        return acc;
      }, {});

    const buildDesignFilteredBucket = (levels, designValue, mapItem, itemsKey) =>
      (levels || [])
        .filter(l => Array.isArray(l.design) && l.design.includes(designValue))
        .reduce((acc, level) => {
          acc[formatScnearioLevelToFilterName(level.name)] = (level[itemsKey] || []).map(mapItem);
          return acc;
        }, {});

    if (gameType === 'Balanced Equation') {
      console.log('[add.js filterItems equation]', {
        beGameScenarioId,
        len: beGameScenarioLevels?.length,
        first: beGameScenarioLevels?.[0] && {
          name: beGameScenarioLevels[0].name,
          entity_type: beGameScenarioLevels[0].entity_type,
          be_questions_len: beGameScenarioLevels[0].be_questions?.length,
        },
        targetEntityType,
      });
    }
    if (gameType === 'Balanced Equation' && beGameScenarioLevels?.length) {
      const filteredLevels = filterLevelsByEntity(beGameScenarioLevels);
      // Levels whose name contains "income statement impact" are
      // scoped to the Income Statement Impact decision type only —
      // every other decision type hides them from its scenario list.
      const isIncomeStatementImpactLevel = (l) =>
        l?.name?.toLowerCase?.()?.includes?.('income statement impact') ?? false;
      const generalLevels = filteredLevels.filter(l => !isIncomeStatementImpactLevel(l));
      const incomeStatementImpactLevels = filteredLevels.filter(isIncomeStatementImpactLevel);
      const generalBucket = buildLevelsBucket(generalLevels, mapBeQuestionToFilterItem, 'be_questions');
      const incomeStatementImpactBucket = buildLevelsBucket(incomeStatementImpactLevels, mapBeQuestionToFilterItem, 'be_questions');
      return {
        'Increase or Decrease': generalBucket,
        'Debits & Credits': generalBucket,
        'Accounts Impacted': generalBucket,
        'Income Statement Impact': incomeStatementImpactBucket,
        'All Types': generalBucket,
      };
    }

    if (gameType === 'The Principles') {
      console.log('[add.js filterItems principles]', {
        principlesGameScenarioId,
        len: principlesGameScenarioLevels?.length,
        first: principlesGameScenarioLevels?.[0] && {
          name: principlesGameScenarioLevels[0].name,
          entity_type: principlesGameScenarioLevels[0].entity_type,
          transactions_len: principlesGameScenarioLevels[0].transactions?.length,
        },
        targetEntityType,
      });
    }
    if (gameType === 'The Principles' && principlesGameScenarioLevels?.length) {
      const filteredLevels = filterLevelsByEntity(principlesGameScenarioLevels);
      return {
        'Revenue & Expense Recognition': buildDesignFilteredBucket(filteredLevels, '2', mapPrinciplesTransactionToFilterItem, 'transactions'),
        'Assumptions, Principles, & Constraints': buildDesignFilteredBucket(filteredLevels, '4', mapPrinciplesTransactionToFilterItem, 'transactions'),
      };
    }

    return {};
  }, [gameType, beGameScenarioLevels, principlesGameScenarioLevels, targetEntityType]);
  
  const handleAddGame = async ({
    name,
    description,
    startTime,
    endTime,
    gameType
  }) => {
    setGameType(gameType);
    setIsLoading(true);
    let resultCreateGame;

    const courseIds = courses.map(course => course.id);
    const resultCreateScenario = await createScenarioMutation.mutateAsync({name, courses: courseIds});
    if(resultCreateScenario?.id) {
      if(!games.length) {
        const { data: coursesSetupData } = await refetchCoursesSetup();

        if (coursesSetupData && coursesSetupData.length > 0) {
          for (const course of coursesSetupData) {
            await scenarioAddCourseMutation.mutateAsync({
              scenarioId: course.id,
              courseId: courseIds[0]
            });
          }
        }
      }

      // Ensure the single-source template scenario for this game type
      // is linked to the instructor's course. The first-game block
      // above only runs for an instructor's very first assignment, so
      // template scenarios added later would never get linked. Probe
      // the instructor endpoint; an empty response means the scenario
      // isn't in this instructor's scope, so call scenarioAddCourse
      // to link it.
      const templateScenarioName =
        gameType === 'Account Classification' ? 'classification-game' :
        gameType === 'Normal Balance' ? 'normal-game' :
        gameType === 'Balanced Equation' ? 'be-game' :
        gameType === 'The Principles' ? 'principles-game' :
        null;
      const templateScenario = templateScenarioName
        ? scenarios?.find(s => s.name === templateScenarioName)
        : null;
      // Cache the probe response so the auto-create flow further down
      // can reuse it instead of re-fetching the same template levels.
      let cachedTemplateLevels = null;
      if (templateScenario) {
        const probeLevels = await getInstructorScenarioLevels({ scenarioId: templateScenario.id });
        if (probeLevels?.length) {
          cachedTemplateLevels = probeLevels;
        } else {
          await scenarioAddCourseMutation.mutateAsync({
            scenarioId: templateScenario.id,
            courseId: courseIds[0]
          });
          // After linking, the probe scenarioId is now in scope —
          // fetch once so cachedTemplateLevels has data the auto-
          // create block can reuse.
          cachedTemplateLevels = await getInstructorScenarioLevels({ scenarioId: templateScenario.id });
          // The filterItems useMemo reads from the same instructor-scoped
          // observer above. Invalidate so it refetches against the freshly
          // linked template — otherwise the observer keeps its pre-link
          // empty response and the BE/Principles level modal renders with
          // no scenarios or transactions until the user hard-refreshes.
          await queryClient.invalidateQueries({ queryKey: ['instructorScenarioLevels', templateScenario.id] });
        }
      }

      setCreatedScenarioId(resultCreateScenario?.id);
      resultCreateGame = await createGameMutation.mutateAsync({
        name,
        description,
        startTime,
        endTime,
        gameType,
        scenarioId: resultCreateScenario.id,
        courseId: courses[0]?.id
      }).catch((error) => {
        console.error('Caught error with mutateAsync:', error);
      });
      if (createGameMutation.isError) {
        console.error('Error:', createGameMutation.error);
      }
      setGameCreatedId(resultCreateGame.id);
      // Store the created game ID for the onboarding tour's final step
      if (sessionStorage.getItem('onboardingTourActive') === 'true') {
        sessionStorage.setItem('onboardingTourGameId', String(resultCreateGame.id));
      }
      if(gameType === 'Balanced Equation' || gameType === 'The Principles') {
        setIsModalShown(true);
        refetchScenarios();
      } else {
        let name;
        switch(gameType) {
          case 'Account Classification':
            name = 'class-elements';
            break;
          case 'Normal Balance':
            name = 'norm-elements';
            break;
          default:
            name = 'class-elements';
        }

        const resultCreateScenarioLevels = await createScenarioLevelsMutation.mutateAsync({ 
          name,
          levelNumber: 1,
          scenarios: [resultCreateScenario?.id],
          description: scenarioLevelsDescription[gameType][0],
          design: ['1']
        });
        const gameTypeShortened = gameType.includes('Account Classification') 
          ? 'classification'
          : gameType.includes('Normal Balance')
            ? 'normal'
            : '';
        if(resultCreateScenarioLevels?.id) {
          // Pull seed elements + drop-zone options from the
          // classification-game / normal-game scenario's
          // 'classification-normal-template' level. Same single source
          // the "Add a Level" modal pulls templates from for the
          // "Accounts" decision type.
          const sourceScenarioName = gameTypeShortened === 'normal' ? 'normal-game' : 'classification-game';
          const sourceScenario = scenarios?.find(item => item.name === sourceScenarioName);
          // Reuse the probe response when it's the same scenario as
          // the auto-create source. Same scenario in practice for
          // Classification (probe template === source template), so
          // we save one GET. Falls back to a fresh fetch otherwise.
          const refLevels = sourceScenario
            ? (cachedTemplateLevels && templateScenario?.id === sourceScenario.id
                ? cachedTemplateLevels
                : await getInstructorScenarioLevels({ scenarioId: sourceScenario.id }))
            : [];
          const referenceLevel = (refLevels || []).find(l => l.name === 'classification-normal-template');
          const referenceQuestions = gameTypeShortened === 'classification'
            ? referenceLevel?.questions || []
            : referenceLevel?.normal_questions || [];
          const referenceOptions = gameTypeShortened === 'classification'
            ? referenceLevel?.options || []
            : referenceLevel?.normal_options || [];

          try {
            // Pick the 5 financial-statement elements (Assets,
            // Liabilities, Equity, Revenues, Expenses) from the
            // template's questions/options. The match is narrowed by
            // account_item.entity_type so corporate / non-corporate
            // variants of an item with the same name don't both
            // qualify for the instructor's selected business type.
            const allReferenceItems = [...referenceQuestions, ...referenceOptions];
            const newQuestions = gameCreateItemsQuestionsColumns
              .map((name, index) => {
                const matched = allReferenceItems.find(q =>
                  q.account_item?.name === name &&
                  matchesEntityType(q.account_item?.entity_type, targetEntityType)
                );
                return matched ? { order: matched.order || index + 1, accountItemId: matched.account_item.id } : null;
              })
              .filter(Boolean);

            if (newQuestions.length) {
              await createItemQuestionsBulkMutation.mutateAsync({
                gameType: gameTypeShortened,
                questions: newQuestions,
                scenarioLevelId: resultCreateScenarioLevels.id
              });
            }
          } catch (error) {
            console.log('Some mutations failed:', error);
          } finally {
            // Drop-zone options: Balance Sheet / Income Statement for
            // classification, Debit / Credit for normal balance.
            const gameCreateItemsOptions = gameTypeShortened === 'classification'
              ? gameCreateItemsOptionsClassification
              : gameCreateItemsOptionsNormal;

            try {
              const newOptions = gameCreateItemsOptions
                .map((name, index) => {
                  const matched = referenceOptions.find(opt =>
                    opt.account_item?.name === name &&
                    matchesEntityType(opt.account_item?.entity_type, targetEntityType)
                  );
                  return matched ? { order: matched.order || index + 1, accountItemId: matched.account_item.id } : null;
                })
                .filter(Boolean);

              if (newOptions.length) {
                await createItemOptionsBulkMutation.mutateAsync({
                  gameType: gameTypeShortened,
                  options: newOptions,
                  scenarioLevelId: resultCreateScenarioLevels.id
                });
              }
            } catch (error) {
              console.log('Some mutations failed:', error);
            }
          }
        }
      }
    }
    setIsLoading(false);

    if(resultCreateGame?.id && (gameType !== 'Balanced Equation' && gameType !== 'The Principles')) {
      // Advance the tour after the game is fully created, before navigating
      if (isTourActive) {
        tourNextStep();
      }
      // Refetch generalInfo before navigating so the edit page mounts
      // with fresh data that includes the just-created game. Without
      // this, edit.js gets the stale cached generalInfo (without the
      // new game), formData stays empty, and the page renders blank
      // until a hard reload. course-setup is not refetched here —
      // creating an assignment doesn't change the template-scenario
      // list returned by that endpoint.
      await queryClient.refetchQueries({ queryKey: ['generalInfo'] });
      navigate(`/instructor/settings/assignment/edit/${resultCreateGame.id}`);
    }
  }

  const handleCloseButtonClick = () => {
    navigate('/instructor/settings/assignments');
  }

  const handleModalCloseButtonClick = () => {
    navigate(`/instructor/settings/assignment/edit/${gameCreatedId}`);
  }

  const handleBalancedPrinciplesAccontSelectSaveButtonClick = async (selectedAccountItems, selectedFilter, selectedFilterParent, accountItemsSavedCallback, gameType, selectedDecisionType, equation) => {
    setIsLoading(true);
    let description;
    const selectedDesignTypes = designTypesMap[gameType]?.[selectedDecisionType] || [];

    if(gameType === 'The Principles') {
      const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
        formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter)
      );
      description = filterScenarioLevel?.description;
    } else {
      description = 'Decisions';
      switch (selectedDecisionType) {
        case 'increase-decrease':
          description += '/Increase or Decrease';
          break;
        case 'debit-credit2':
          description += '/Debits & Credits';
          break;
        case 'accounts-impacted':
          description += '/Accounts Impacted';
          break;
        case 'income-statement-impact':
          description += '/Income Statement Impact';
          break;
        case 'all-types':
          description += '/All Types';
          break;
        default:
          break;
      }
    }

    let name;
    switch(gameType) {
      case 'Balanced Equation':
        name = 'increase-decrease';
        break;
      case 'The Principles':
        // Fallback to 'revenue-expense' since revenue-recognition /
        // expense-recognition are no longer surfaced to the user.
        name = selectedDecisionType || 'revenue-expense';
        break;
      default:
        name = 'increase-decrease';
    }

    const resultCreateScenarioLevels = await createScenarioLevelsMutation.mutateAsync({
      name,
      levelNumber: 1,
      scenarios: [createdScenarioId],
      description,
      design: selectedDesignTypes,
      // BE L1 is always Increase-or-Decrease, so the modal pops the
      // equation picker and forwards the picked 'assets' | 'equity'.
      // Principles / Classification etc. pass undefined so the field
      // stays null on the backend.
      equation
    });
    
    if(resultCreateScenarioLevels?.id) {
      try {
        const gameTypeShortened = gameType.includes('Balanced Equation')
          ? 'equation'
          : gameType.includes('The Principles')
            ? 'principles'
            : '';

        const itemsToProcess = selectedDecisionType === 'assumptions-principles-constraints'
          ? shuffleArray([...selectedAccountItems])
          : selectedAccountItems;

        const newQuestions = itemsToProcess.map((item, index) => ({
          order: item.order || index + 1,
          accountItemId: item.accountItemId || item.id,
        }));

        if (newQuestions.length) {
          await createItemQuestionsBulkMutation.mutateAsync({
            gameType: gameTypeShortened,
            questions: newQuestions,
            scenarioLevelId: resultCreateScenarioLevels.id
          });
        }
      } catch (error) {
        console.log('Some mutations failed:', error);
      } finally {
        if(gameType === 'Balanced Equation') {
          const filterScenarioLevel = (beGameScenarioLevels || []).find(level =>
            level.name.toLowerCase().replace(/\s+/g, '-') === selectedFilter.toLowerCase().replace(/\s+/g, '-')
          );

          let filterScenarioOptions = [];

          if (selectedDesignTypes.includes("1")) {
            filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => option.order >= 1 && option.order <= 5));
          }

          if (selectedDesignTypes.includes("2")) {
            filterScenarioOptions.push(...filterScenarioLevel.be_options.filter(option => [1, 2, 3, 6, 7].includes(option.order)));
          }

          if (selectedDesignTypes.includes("3")) {
            filterScenarioOptions.push(
              ...filterScenarioLevel.be_options.filter(option => option.account_item.account === true)
            );
          }

          filterScenarioOptions = [...new Set(filterScenarioOptions)];
          
          try {
            const newOptions = filterScenarioOptions.map((opt, index) => ({
              order: opt.order || index + 1,
              accountItemId: opt.account_item.id
            }));

            if (newOptions.length) {
              await createItemOptionsBulkMutation.mutateAsync({
                gameType: 'equation',
                options: newOptions,
                scenarioLevelId: resultCreateScenarioLevels.id
              });
            }
          } catch (error) {
            console.log('Some mutations failed:', error);
          }
        } else if (gameType === 'The Principles' && selectedDecisionType === 'assumptions-principles-constraints') {
          const filterScenarioLevel = (principlesGameScenarioLevels || []).find(level =>
            formatScnearioLevelToFilterName(level.name) === formatScnearioLevelToFilterName(selectedFilter)
          );

          const sourceOptions = filterScenarioLevel?.principles_options || [];

          try {
            const newOptions = sourceOptions
              .map((opt, index) => ({
                order: opt.order || index + 1,
                accountItemId: opt.account_item?.id,
              }))
              .filter((o) => o.accountItemId);

            if (newOptions.length) {
              await createItemOptionsBulkMutation.mutateAsync({
                gameType: 'principles',
                options: newOptions,
                scenarioLevelId: resultCreateScenarioLevels.id
              });
            }
          } catch (error) {
            console.log('Some mutations failed:', error);
          }
        }
      }
    }

    setIsLoading(false);
    // Same staleness fix as the auto-create path: refetch generalInfo
    // BEFORE the savedCallback (which navigates via handleModalClose-
    // ButtonClick) so the edit page mounts with games[] that includes
    // the just-created BE/Principles assignment. The trailing
    // navigate() that used to live here is dropped — savedCallback
    // already navigates.
    await queryClient.refetchQueries({ queryKey: ['generalInfo'] });
    accountItemsSavedCallback();
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="adjustment-content-container"
      showPagination={false}
      secondButtonName="Close"
      onSecondButtonClick={handleCloseButtonClick}
    >
      <InstructorAssignmentForm
        games={games}
        gamesLimit={courses?.[0]?.game_limit || 5}
        onSave={handleAddGame}
        isButtonLoading={isLoading}
      />

      {isModalShown && gameType === 'Balanced Equation' && (
        <BalancedEquationLevelModal
          scenarioLevelNumber={1}
          onCloseButtonClick={handleModalCloseButtonClick}
          onSaveButtonClick={handleBalancedPrinciplesAccontSelectSaveButtonClick}
          isButtonLoading={isLoading}
          filterItems={filterItems}
        />
      )}

      {isModalShown && gameType === 'The Principles' && (
        <PrinciplesLevelModal
          scenarioLevelNumber={1}
          onCloseButtonClick={handleModalCloseButtonClick}
          onSaveButtonClick={handleBalancedPrinciplesAccontSelectSaveButtonClick}
          isButtonLoading={isLoading}
          filterItems={filterItems}
        />
      )}

      {isModalShown && gameType === 'Account Classification' && (
        <ClassificationLevelModal
          scenarioLevelNumber={1}
          onCloseButtonClick={handleModalCloseButtonClick}
          onSaveButtonClick={handleBalancedPrinciplesAccontSelectSaveButtonClick}
          isButtonLoading={isLoading}
          filterItems={filterItems}
        />
      )}

      {isModalShown && gameType === 'Normal Balance' && (
        <NormalBalanceLevelModal
          scenarioLevelNumber={1}
          onCloseButtonClick={handleModalCloseButtonClick}
          onSaveButtonClick={handleBalancedPrinciplesAccontSelectSaveButtonClick}
          isButtonLoading={isLoading}
          filterItems={filterItems}
        />
      )}

      {isModalShown
        && gameType !== 'Balanced Equation'
        && gameType !== 'The Principles'
        && gameType !== 'Account Classification'
        && gameType !== 'Normal Balance' && (
        <InstructorLevelItemsModal
          scenarioLevelNumber={1}
          onCloseButtonClick={handleModalCloseButtonClick}
          onSaveButtonClick={handleBalancedPrinciplesAccontSelectSaveButtonClick}
          isButtonLoading={isLoading}
          gameType={gameType}
          filterItems={filterItems}
        />
      )}
    </SubPagesLayout>
  );
}
